import { useTranslation } from 'react-i18next';
import { KoobButton, KoobId, KoobInputNumber, KoobTableAction } from '@koob/ui';
import React, { useEffect, useState } from 'react';
import Header from './Partials/Header';
import RequestLoadError from './Partials/RequestLoadError';
import { post } from '../../../api/node';
import { useApp, useError, useSnack } from '../../../hooks';
import { useAsync } from 'react-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import DistributionBrowser from './Partials/DistributionBrowser';
import DistributionSelected from './Partials/DistributionSelected';
import ProductStateBadge from './Partials/ProductStateBadge';
import { Spinner } from '@koob/margaret';
import {
  getToConnectionRequestById,
  updateToConnectionRequest,
} from '../../../api/node/toConnectionRequest';
import { PER_PAGE_FOR_LIST } from 'constants';
import ToConnectionRequestUpdating from './Partials/ToConnectionRequestUpdating';
import { ExperienceTypeEnum } from '@koob/enums';
import { decodeId } from 'utils';

const getList = async ({ filters }) => {
  return await post('/experiences/liste', filters);
};

const ToConnectionRequestExperience = () => {
  const { t } = useTranslation('toConnectionRequest');
  const {
    isAllowedTo,
    canManageToConnectionRequest,
    user,
    selectedDistributionOrganization,
    currentOrganization,
  } = useApp();
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();
  const { requestId } = useParams();
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const isAllowedToManage = () => {
    return (
      isAllowedTo('canManageToConnectionRequest') ||
      canManageToConnectionRequest(user)
    );
  };
  const dmcId =
    selectedDistributionOrganization?.value ?? currentOrganization?.id;

  const {
    data,
    isLoading,
    error: loadError,
    reload,
  } = useAsync({
    promiseFn: getToConnectionRequestById,
    requestId: requestId,
    dmcId: decodeId(dmcId),
  });
  const request = data?.data;

  const formatExperienceData = (listExperience) => {
    return (listExperience || []).map((node) => {
      if (node?.experience) {
        return {
          id: node.experience?.id,
          name: node.experience?.name,
          state: node.experience?.state,
          country: node.experience?.inCity?.country?.alpha2,
          city: node.experience?.inCity?.title,
          margin: node.margin,
          isMarginPercent: node.isMarginPercent,
          periods: [],
        };
      } else {
        return {
          id: node?.id,
          name: node?.name,
          state: node?.state,
          country: node?.inCity?.country?.alpha2,
          city: node?.inCity?.title,
          experienceType: node?.isExtra
            ? ExperienceTypeEnum.EXTRA
            : node?.experienceType,
          margin: null,
          isMarginPercent: null,
        };
      }
    });
  };

  useEffect(() => {
    if (request) {
      setGlobalMargin(request.margin ?? 0);
      setIsGlobalMarginPercent(request.isMarginPercent ?? false);
    }
    if (request?.requestExperiences) {
      let formattedExperience = formatExperienceData(
        request?.requestExperiences,
      );
      setSelectedExperiences(formattedExperience);
    }
  }, [request]);

  const [experiences, setExperiences] = useState([]);
  const [selectedExperiences, setSelectedExperiences] = useState(
    request?.experiences || [],
  );
  const [globalMargin, setGlobalMargin] = useState(0);
  const [isGlobalMarginPercent, setIsGlobalMarginPercent] = useState(false);
  const [saving, setSaving] = useState(false);

  const changedExperiences = selectedExperiences?.filter((experience) => {
    const originalExperience = request?.requestExperiences?.find(
      (originalExperience) =>
        originalExperience.experience.id === experience.id,
    );
    return (
      !originalExperience ||
      (experience.margin !== undefined &&
        originalExperience?.margin !== experience.margin) ||
      (experience.isMarginPercent !== undefined &&
        originalExperience?.isMarginPercent !== experience.isMarginPercent)
    );
  });

  const save = async (state, comment) => {
    setSaving(true);

    try {
      await updateToConnectionRequest({
        toConnectionRequestId: requestId,
        payload: {
          margin: globalMargin ? parseFloat(globalMargin) : 0,
          isMarginPercent: isGlobalMarginPercent,
          state: state ?? request.state,
          explaination: comment?.reason,
          toConnectionRequestExperiences: [
            ...changedExperiences.map((exp) => ({
              id: exp.id,
              margin: exp.margin ? parseFloat(exp.margin) : 0,
              isMarginPercent: exp.isMarginPercent,
              distribute: true,
            })),
            ...(request.requestExperiences
              ?.filter(
                (exp) =>
                  !selectedExperiences.some((e) => e.id === exp.experience.id),
              )
              .map((requestExperience) => ({
                id: requestExperience.experience.id,
                distribute: false,
              })) ?? []),
          ],
        },
      });

      notify(t('updated'));
      navigate(-1);
    } catch (error) {
      sendErrorSnack(error);
    } finally {
      setSaving(false);
    }
  };

  const setExperienceAttribute = (experienceId, attribute, value) => {
    setSelectedExperiences((current) =>
      current.map((experience) => {
        if (experience.id === experienceId) {
          return {
            ...experience,
            [attribute]: value,
          };
        }
        return experience;
      }),
    );
  };

  const isExperienceSelected = (experienceId) => {
    return selectedExperiences.some((e) => e.id === experienceId);
  };

  const unselectExperience = (experienceId) => {
    setSelectedExperiences(
      selectedExperiences.filter((e) => e.id !== experienceId),
    );
  };

  const removeSelectedExperiences = (experienceIds) => {
    setSelectedExperiences(
      selectedExperiences.filter((e) => !experienceIds.includes(e.id)),
    );
    notify(t('removeSelectedSuccess', { count: experienceIds.length }));
  };

  const selectExperience = (experience) => {
    if (!isExperienceSelected(experience.id)) {
      experience.margin = globalMargin;
      experience.isMarginPercent = isGlobalMarginPercent;
      experience.periods = [];
      setSelectedExperiences([...selectedExperiences, experience]);
    } else {
      unselectExperience(experience.id);
    }
  };

  const selectAllShownExperiences = (experienceList) => {
    const experiencesNotSelected = experienceList.filter(
      (experience) => !isExperienceSelected(experience.id),
    );
    experiencesNotSelected.forEach((experience) => {
      experience.margin = globalMargin;
      experience.isMarginPercent = isGlobalMarginPercent;
      experience.periods = [];
    });
    setSelectedExperiences([...selectedExperiences, ...experiencesNotSelected]);
  };

  const BrowserHeadings = [
    {
      slug: 'name',
      label: t('experienceName'),
      width: '100px',
      cannotBeReordered: true,
    },
    {
      slug: 'country',
      label: t('countryName'),
      width: '60px',
      cannotBeReordered: true,
    },
    {
      slug: 'city',
      label: t('cityName'),
      width: '100px',
      cannotBeReordered: true,
    },
    {
      slug: 'experienceType',
      label: t('experienceType'),
      width: '50px',
      cannotBeReordered: true,
    },
    {
      slug: 'state',
      label: t('state'),
      width: '70px',
      cannotBeReordered: true,
    },
    {
      slug: 'selected',
      label: t('enabled'),
      width: '40px',
      cannotBeReordered: true,
    },
  ];

  const selectedDatasHeadings = [
    {
      slug: 'name',
      label: t('experienceName'),
      width: '80px',
      cannotBeReordered: true,
    },
    {
      slug: 'country',
      label: t('countryName'),
      width: '60px',
      cannotBeReordered: true,
    },
    {
      slug: 'city',
      label: t('cityName'),
      width: '60px',
      cannotBeReordered: true,
    },
    {
      slug: 'state',
      label: t('state'),
      width: '70px',
      cannotBeReordered: true,
    },
    {
      slug: 'margin',
      label: t('margin'),
      width: '40px',
      cannotBeReordered: true,
    },
    {
      slug: 'isMarginPercent',
      label: t('percent'),
      width: '60px',
      cannotBeReordered: true,
    },
    {
      slug: 'selected',
      label: t('enabled'),
      width: '40px',
      cannotBeReordered: true,
    },
  ];

  const tableExperiences = (experiences || []).map((node) => ({
    id: node?.id,
    name: {
      render: () => (
        <div className="flex flex-col items-start">
          <span className="font-medium">{node?.name}</span>
          <KoobId id={node?.id} />
        </div>
      ),
    },
    country: {
      value: node?.country,
    },
    city: {
      value: node?.city,
    },
    experienceType: {
      value: node?.isExtra ? ExperienceTypeEnum.EXTRA : node?.experienceType,
    },
    state: {
      render: () => <ProductStateBadge state={node?.state} />,
    },
    selected: {
      render: () =>
        isAllowedToManage() ? (
          <KoobTableAction
            icon={
              <i
                className={`fa-regular fa-eye ${
                  isExperienceSelected(node?.id) ? 'text-orange-500' : ''
                }`}
              />
            }
            label={t('enabled')}
            onClick={() => {
              selectExperience(node);
            }}
          />
        ) : null,
    },
  }));

  const experiencesSelectedDataTable = (selectedExperiences || []).map(
    (node, index) => ({
      id: node?.id,
      name: {
        render: () => (
          <div className="flex flex-col items-start">
            <span className="font-medium">{node?.name}</span>
            <KoobId id={node?.id} />
          </div>
        ),
      },
      country: {
        value: node?.country,
      },
      city: {
        value: node?.city,
      },
      state: {
        render: () => <ProductStateBadge state={node?.state} />,
      },
      margin: {
        render: () => (
          <KoobInputNumber
            min={0}
            value={node?.margin}
            onValueChange={(value) =>
              setExperienceAttribute(node.id, 'margin', value ?? '')
            }
            disabled={!isAllowedToManage()}
          />
        ),
      },
      isMarginPercent: {
        render: () => (
          <input
            type="checkbox"
            checked={node?.isMarginPercent}
            onChange={() =>
              setExperienceAttribute(
                node.id,
                'isMarginPercent',
                !node.isMarginPercent,
              )
            }
            disabled={!isAllowedToManage()}
          />
        ),
      },
      selected: {
        render: () =>
          isAllowedToManage() ? (
            <KoobTableAction
              icon="delete"
              variant="danger"
              label={t('misc:delete')}
              onClick={() => unselectExperience(node.id)}
            />
          ) : null,
      },
    }),
  );

  const limitPerPage = PER_PAGE_FOR_LIST;

  const requestFilters = {
    cities: filters?.location?.kind === 'city' ? [filters?.location?.id] : [],
    countries:
      filters?.location?.kind === 'country' ? [filters?.location?.id] : [],
    // Typed text wins; a product picked in the location box searches its name.
    search:
      filters?.search ||
      (filters?.location?.kind === 'experience'
        ? filters?.location?.title
        : ''),
    types: filters?.type?.value ? [filters?.type?.value] : [],
    // KD-3547 — no state filter: a DMC distributes experiences it is still
    // preparing (in progress / stand by), not only the available ones.
    page: page,
    perPage: limitPerPage,
    dmcId: decodeId(selectedDistributionOrganization?.value),
    shouldSearchAdditionalFields: false,
  };

  const {
    data: experiencesList,
    reload: reloadExperiences,
    isLoading: experienceLoading,
  } = useAsync({
    promiseFn: getList,
    filters: requestFilters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : loadError ? (
        <RequestLoadError onRetry={reload} />
      ) : (
        <div className="px-4">
          <Header
            request={request}
            save={save}
            isLoading={saving}
            isAllowedToManage={isAllowedToManage()}
            selectedDistributionOrganization={selectedDistributionOrganization}
          />

          {!request?.activeJob ? (
            <>
              {isAllowedToManage() && (
                <div className="my-5 flex justify-end gap-5">
                  <KoobButton type="button" variant="secondary" asChild>
                    <Link to={-1}>{t('misc:cancel')}</Link>
                  </KoobButton>
                  <KoobButton
                    onClick={() => save()}
                    variant="primary"
                    disabled={saving}
                  >
                    {t('misc:save')}
                  </KoobButton>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <DistributionSelected
                  selectedDatasHeadings={selectedDatasHeadings}
                  selectedDatas={experiencesSelectedDataTable}
                  tabTitle={'selectedExperience'}
                  onRemoveSelected={
                    isAllowedToManage() ? removeSelectedExperiences : undefined
                  }
                />

                {isAllowedToManage() && (
                  <DistributionBrowser
                    browserDatas={experiences}
                    setBrowerDatas={setExperiences}
                    BrowserHeadings={BrowserHeadings}
                    browserDatasList={experiencesList}
                    isLoading={experienceLoading}
                    selectAllShownItems={selectAllShownExperiences}
                    dataTableDatas={tableExperiences}
                    globalMargin={globalMargin}
                    setGlobalMargin={setGlobalMargin}
                    isGlobalMarginPercent={isGlobalMarginPercent}
                    setIsGlobalMarginPercent={setIsGlobalMarginPercent}
                    formatBrowerData={formatExperienceData}
                    setFilters={setFilters}
                    setPage={setPage}
                    filters={filters}
                    limitPerPage={PER_PAGE_FOR_LIST}
                    reload={reloadExperiences}
                    tabTitle={'browseExperience'}
                    kind={'experience'}
                    searcTranslate={'experiences'}
                  />
                )}
              </div>
            </>
          ) : (
            <ToConnectionRequestUpdating />
          )}
        </div>
      )}
    </>
  );
};

export default ToConnectionRequestExperience;
